/* Primitives shared by every trace surface. These live outside TraceDetail so
   the per-kind views can use them without importing a 1,200-line module — and
   so there is exactly one definition of a duration chip, a truncated payload
   and a kind glyph anywhere in the app. */

import React, { useState } from "react";
import { Clock, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/shared/lib/cn";
import { fmtMs } from "@/shared/lib/runs";
import { APP_ICONS } from "@/shared/lib/app-icons";
// Moved to shared/ so the showcase can render the real component; re-exported
// so every trace surface keeps importing all its atoms from this one module.
export { StatusPill } from "@/shared/components/trace-atoms";
import { kindOf, toneFor, TONES } from "@/shared/lib/trace-kinds";

import type { ReactNode, SyntheticEvent } from "react";

import type { AppId, TraceKind } from "@/shared/types";
import type { ModelTier } from "@/shared/types/models";
import type { SpanStatus } from "../types";

export const chipCls =
  "inline-flex items-center gap-1 h-[20px] px-2 rounded-full bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] font-mono text-[11px] text-[var(--text-secondary)] whitespace-nowrap";

/** Which of an agent's two models wrote a generation: "fast" or "slow". */
export function TierChip({ tier, className = "" }: { tier: ModelTier; className?: string }) {
  return <span className={cn(chipCls, className)}>{tier}</span>;
}

export function DurChip({ ms }: { ms: number }) {
  return (
    <span className={chipCls}>
      <Clock className="w-[10px] h-[10px] text-[var(--text-tertiary)]" strokeWidth={1.75} />
      {fmtMs(ms)}
    </span>
  );
}

/* span, not <button> — it renders inside clickable run rows, and nested
   interactive elements are invalid HTML. */
/* A recorded run's id is a UUID: one line shows its head and tail, the copy is the whole id. */
const shortId = (id: string): string => (id.length > 13 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id);

export function CopyId({ id, label = "Trace" }: { id: string; label?: string }) {
  const copy = (e: SyntheticEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(id);
    toast.success(`${label} ${id} copied`);
  };
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={copy}
      onKeyDown={(e) => e.key === "Enter" && copy(e)}
      title={`Copy ${label.toLowerCase()} ID ${id}`}
      className="press inline-flex items-center gap-1 whitespace-nowrap font-mono text-[11.5px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer"
    >
      <Copy className="w-[10.5px] h-[10.5px] shrink-0" strokeWidth={1.75} />
      {shortId(id)}
    </span>
  );
}

/* Type-coloured icon square. App tools (gmail.send, notion.create_page, …)
   render their real brand logo; everything else takes its glyph and tone from
   the kind registry, so a kind can never look like two different things in two
   different views. */
export function KindIcon({ kind, status, tool, size = 24 }: {
  kind: TraceKind;
  status?: SpanStatus;
  tool?: string;
  size?: number;
}) {
  const family = tool ? tool.split(".")[0] : null;
  // The tool family is a runtime string; only some of them name an app.
  const appLogo = family ? APP_ICONS[family as AppId] : null;
  const { Icon } = kindOf(kind);
  const tone = TONES[toneFor(kind, status)];
  return (
    <span
      className={`shrink-0 rounded-[7px] ring-1 flex items-center justify-center ${tone}`}
      style={{ width: size, height: size }}
    >
      {appLogo && status !== "error" ? (
        // There is only a logo to show when there was a family to look it up by.
        <img src={appLogo} alt={family as string} style={{ width: size * 0.58, height: size * 0.58 }} />
      ) : (
        <Icon style={{ width: size * 0.5, height: size * 0.5 }} strokeWidth={1.75} />
      )}
    </span>
  );
}


export function StatusDot({ status }: { status?: SpanStatus }) {
  if (status === "error") return <span className="shrink-0 w-[5px] h-[5px] rounded-full bg-[var(--danger)]" />;
  if (status === "pending") return <span className="shrink-0 w-[5px] h-[5px] rounded-full bg-[var(--warn)]" />;
  return null;
}

/* Long strings collapse behind a character count — traces stay scannable and
   full payloads stay one click away. Never truncate silently: the count is what
   tells the reader there is more, and how much more. */
export function TruncText({ text, limit = 280, mono = false, className = "" }: {
  text?: string | number | null;
  limit?: number;
  mono?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (text == null) return null;
  const str = String(text);
  const truncated = !open && str.length > limit;
  return (
    <span className={`${mono ? "font-mono" : ""} whitespace-pre-wrap break-words ${className}`}>
      {truncated ? `${str.slice(0, limit).trimEnd()}…` : str}
      {truncated && (
        <button
          onClick={() => setOpen(true)}
          className="press ml-1.5 align-baseline inline-flex px-1.5 py-[1px] rounded-[5px] bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] font-mono text-[10.5px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          Show all ({str.length.toLocaleString()} chars)
        </button>
      )}
    </span>
  );
}

export function FieldRow({ label, children }: { label: ReactNode; children?: ReactNode }) {
  return (
    <div className="py-2 border-b hairline last:border-b-0">
      <div className="text-[11.5px] text-[var(--text-tertiary)] mb-1">{label}</div>
      <div className="text-[13px] text-[var(--text-primary)]">{children}</div>
    </div>
  );
}

export function EmptyNote({ children }: { children?: ReactNode }) {
  return <div className="text-[12.5px] italic text-[var(--text-quaternary)] py-1">{children}</div>;
}

export function ErrorNote({ text }: { text?: string | null }) {
  return (
    <div className="rounded-[8px] bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)] px-3 py-2.5">
      <div className="text-[11.5px] font-medium text-[var(--danger)] mb-0.5">Error</div>
      <TruncText mono text={text} className="text-[12.5px] leading-[1.6] text-[var(--danger)]" />
    </div>
  );
}

/* Pass / fail mark used by the verifier and by contract checks. Deliberately
   the same shape in both places — a check is a check. */
export function PassPill({ pass, children }: { pass?: boolean; children?: ReactNode }) {
  const tone = pass
    ? "bg-[var(--success-bg)] ring-[var(--success-ring)] text-[var(--success)]"
    : "bg-[var(--danger-bg)] ring-[var(--danger-ring)] text-[var(--danger)]";
  return (
    <span className={`inline-flex items-center gap-1 h-[20px] px-2 rounded-full ring-1 font-mono text-[11px] font-medium ${tone}`}>
      {children ?? (pass ? "pass" : "fail")}
    </span>
  );
}

/* Small labelled bar — used for score targets and cache/context ratios. */
export function MiniBar({ value, tone = "success", width = 52 }: {
  value: number;
  tone?: "success" | "warn" | "info" | "danger";
  width?: number;
}) {
  const fill = { success: "bg-[var(--success)]", warn: "bg-[var(--warn)]", info: "bg-[var(--info)]", danger: "bg-[var(--danger)]" }[tone];
  return (
    <span className="h-[4px] rounded-full bg-[var(--surface-2)] overflow-hidden inline-block align-middle" style={{ width }}>
      <span className={`block h-full rounded-full ${fill}`} style={{ width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` }} />
    </span>
  );
}
